<template>
  <section 
    id="contact" 
    class="py-20 w-full relative overflow-hidden font-poppins"
    style="position: relative; z-index: 7;"
  >
    <!-- Improved background with gradient and subtle texture -->
    <div class="absolute inset-0 bg-gradient-to-b from-white via-site-offwhite/50 to-white"></div>
    <div class="absolute inset-0 opacity-5" style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D9A68C\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v6h6v-6h-6zm6 6v6h-6v-6h6zm-6-6v-6h6v6h-6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"></div>
    
    <!-- Smooth transitions between sections -->
    <div class="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-white to-transparent"></div>
    
    <div class="container mx-auto px-4 md:px-8 lg:px-16 max-w-[1600px] relative z-10">
      <!-- Section header with refined styling -->
      <div class="mb-16 text-center">
        <h2 class="text-4xl md:text-5xl font-bold text-site-text mb-4">Contact</h2>
        <div class="h-1 w-24 mx-auto bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
        <p class="text-center text-site-text-light mt-6 text-lg max-w-3xl mx-auto">
          I'd love to hear from you. Please feel free to reach out with any questions or to book a session.
        </p>
      </div>
      
      <!-- Contact Content Grid - 2 columns on larger screens -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <!-- Left column - Contact info cards (2 cols) -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Contact Methods -->
          <div class="mb-8">
            <h3 class="text-2xl font-bold text-site-text mb-6 inline-block relative">
              Get In Touch
              <span class="absolute -bottom-1 left-0 w-24 h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
            </h3>
            
            <!-- Email -->
            <div class="contact-card group">
              <div class="contact-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="text-lg font-semibold text-site-text mb-1">Email</h4>
                <a href="mailto:josh@sherbornetherapy.com" class="text-site-text-light hover:text-[#D2919A] transition-colors duration-300">
                 josh@sherbornetherapy.com
                </a>
              </div>
            </div>

          </div>

          
          <!-- Initial Session Info with enhanced styling -->
          <div class="bg-white rounded-lg p-6 shadow-sm border border-site-border/20">
            <h4 class="flex items-center text-xl font-semibold text-site-text mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#D2919A] mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Initial Session
            </h4>
            <p class="text-site-text-light">
              After contacting me, we'll arrange an initial session to discuss your needs and how we might work together. This is also an opportunity for you to ask any questions you might have.
            </p>
          </div>
        </div>
        
        <!-- Right column - Contact form (3 cols) -->
        <div class="lg:col-span-3">
          <div class="bg-white rounded-lg shadow-sm border border-site-border/20 p-6 md:p-8">
            <h3 class="text-2xl font-bold text-site-text mb-6 inline-block relative">
              Send a Message
              <span class="absolute -bottom-1 left-0 w-24 h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
            </h3>
            
            <!-- Contact Form with enhanced styling -->
            <form @submit.prevent="submitForm" class="space-y-6">
              <!-- Name Field -->
              <div class="form-group">
                <label for="name" class="form-label">Name</label>
                <div class="relative">
                  <div class="form-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="name"
                    v-model="formData.name"
                    placeholder="Your name"
                    required
                    class="form-input"
                  />
                </div>
              </div>
              
              <!-- Email Field -->
              <div class="form-group">
                <label for="email" class="form-label">Email</label>
                <div class="relative">
                  <div class="form-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    id="email"
                    v-model="formData.email"
                    placeholder="Your email"
                    required
                    class="form-input"
                  />
                </div>
              </div>

              <!-- Phone Field -->
              <div class="form-group">
                <label for="phone" class="form-label">Phone <span class="text-site-text-light text-sm">(optional)</span></label>
                <div class="relative">
                  <div class="form-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    v-model="formData.phone"
                    placeholder="Your phone number"
                    class="form-input"
                  />
                </div>
              </div>
              
              <!-- Session Type Field -->
              <div class="form-group">
                <label for="sessionType" class="form-label">Interested In</label>
                <div class="relative">
                  <div class="form-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <select
                    id="sessionType"
                    v-model="formData.sessionType"
                    required
                    class="form-select"
                  >
                    <option value="" disabled selected>Select session type</option>
                    <option value="in-person">In-Person Session</option>
                    <option value="online">Online Session</option>
                    <option value="walk-talk">Telephone Service</option>
                    <option value="not-sure">Not Sure Yet</option>
                  </select>
                </div>
              </div>
              
              <!-- Message Field -->
              <div class="form-group">
                <label for="message" class="form-label">Message</label>
                <textarea
                  id="message"
                  v-model="formData.message"
                  rows="5"
                  placeholder="Please share any details about what you're looking for, questions you have, or preferred times for sessions."
                  required
                  class="form-textarea"
                ></textarea>
              </div>
              
              <!-- Submit Button -->
              <div class="flex justify-center md:justify-start pt-4">
                <button type="submit" class="form-submit-button">
                  <span v-if="!isSubmitting">Send Message</span>
                  <span v-else class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                </button>
              </div>
              
              <!-- Form Messages -->
              <div v-if="formMessage" :class="['p-4 rounded-lg text-center', formError ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800']">
                {{ formMessage }}
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <!-- Privacy Notice with enhanced styling -->
      <div class="mt-16 max-w-3xl mx-auto">
        <div class="p-5 bg-white/80 backdrop-blur-sm rounded-lg border border-site-border/20 shadow-sm">
          <h4 class="flex items-center text-lg font-semibold text-site-text mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A] mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Privacy Notice
          </h4>
          <p class="text-sm text-site-text-light">
            Your privacy is important to me. Any information you provide will be kept confidential and used only to respond to your inquiry. I will never share your personal information with third parties without your explicit consent. By submitting this form, you agree to be contacted via the details you've provided.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ContactSection',
  data() {
    return {
      formData: {
        name: '',
        email: '',
        phone: '',
        sessionType: '',
        message: ''
      },
      isSubmitting: false,
      formMessage: '',
      formError: false
    }
  },
  methods: {
    submitForm() {
      this.isSubmitting = true;
      this.formMessage = '';
      
      // Simulate form submission
      setTimeout(() => {
        this.isSubmitting = false;
        this.formMessage = 'Thank you for your message! I will get back to you soon.';
        
        // Reset form
        this.formData = {
          name: '',
          email: '',
          phone: '',
          sessionType: '',
          message: ''
        };
      }, 1500);
    },
    scrollToContact() {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}
</script>

<style scoped>
/* Contact cards styling */
.contact-card {
  @apply flex items-center p-5 mb-4 bg-white rounded-lg transition-all duration-300 border border-[#D2919A]/20 shadow-sm;
  transform: translateY(0);
}

.contact-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
  border-color: rgba(217,166,140,0.3);
}

.contact-icon {
  @apply w-12 h-12 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center mr-4 flex-shrink-0 shadow-sm;
  transition: transform 0.3s ease;
}

.contact-card:hover .contact-icon {
  transform: scale(1.1);
}

/* Form styling */
.form-group {
  @apply mb-6;
}

.form-label {
  @apply block text-site-text font-medium mb-2;
}

.form-icon {
  @apply absolute top-1/2 left-3 transform -translate-y-1/2 pointer-events-none;
}

.form-input, 
.form-select, 
.form-textarea {
  @apply w-full border border-[#D2919A]/30 rounded-lg py-3 px-4 pl-10 text-site-text focus:ring-2 focus:ring-[#D2919A]/50 focus:border-[#D2919A] transition-all duration-300;
  background-color: rgba(255, 255, 255, 0.8);
}

.form-textarea {
  @apply pl-4 resize-none;
}

.form-input:hover, 
.form-select:hover, 
.form-textarea:hover {
  @apply border-[#D2919A]/50;
}

.form-input:focus, 
.form-select:focus, 
.form-textarea:focus {
  @apply outline-none bg-white;
}

.form-submit-button {
  @apply px-8 py-3 bg-gradient-to-r from-[#D2919A] to-[#D2919A]/90 text-white font-medium rounded-full shadow-md hover:shadow-lg flex items-center justify-center transition-all duration-300 hover:-translate-y-1;
}

.form-submit-button:focus {
  @apply outline-none ring-2 ring-[#D2919A] ring-offset-2;
}

.form-submit-button:disabled {
  @apply opacity-70 cursor-not-allowed;
}
</style>